import { Upload as UploadIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import Papa from 'papaparse';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CsvUploadProvider, useCsvUpload } from '../providers';

import { PreviewStep, PreviewStepProps } from './preview-step';
import { SelectCsvStep } from './select-csv-step';
import { SuccessStep, SuccessStepProps } from './success-step';
import { ValidateCsvStep, ValidateCsvStepProps } from './validate-csv-step';

export interface CsvUploadProps<T> {
  onSubmit: (data: T[]) => Promise<void> | void;
}

export interface CsvUploadForm<T = unknown, S = unknown> {
  results?: T[];
  csvParseResult?: Papa.ParseResult<string[]>;
  validationState?: S;
  file?: File;
}

export type AllCsvUploadProps<T, S> = ValidateCsvStepProps<T, S> &
  CsvUploadProps<T> &
  SuccessStepProps &
  PreviewStepProps<T>;

export const CsvUpload = <T, S>(props: AllCsvUploadProps<T, S>) => {
  return (
    <CsvUploadProvider>
      <CsvUploadChild {...props} />
    </CsvUploadProvider>
  );
};

export const CsvUploadChild = <T, S>({
  onSubmit,
  ValidatorComponent,
  PreviewComponent,
  successMessage,
}: AllCsvUploadProps<T, S>) => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { results, file, reset } = useCsvUpload<T, S>();

  const { t } = useTranslation();

  const steps = [
    t('csvImport.steps.select.label'),
    t('csvImport.steps.validate.label'),
    t('csvImport.steps.preview.label'),
    t('csvImport.steps.success.label'),
  ];

  const handleClose = () => {
    setOpen(false);
    setActiveStep(0);
  };

  const handleSubmit = async () => {
    if (isSubmitting || !results) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(results);
      setActiveStep(3);
      reset();
    } catch (error) {
      console.error(error);
    }

    setIsSubmitting(false);
  };

  const canGoNext = () => {
    if (activeStep === 0) {
      return !!file;
    }

    if (activeStep === 1) {
      return results;
    }

    return true;
  };

  const handleNext = () => {
    if (!canGoNext()) {
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('csvImport.button')}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{t('csvImport.title')}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && <SelectCsvStep />}
          {activeStep === 1 && <ValidateCsvStep ValidatorComponent={ValidatorComponent} />}
          {activeStep === 2 && <PreviewStep PreviewComponent={PreviewComponent} />}
          {activeStep === 3 && <SuccessStep successMessage={successMessage} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          {activeStep > 0 && activeStep < 3 && (
            <Button onClick={handleBack} disabled={isSubmitting}>
              {t('common.back')}
            </Button>
          )}
          {activeStep >= 0 && activeStep < 2 && (
            <Button variant="contained" onClick={handleNext} disabled={!canGoNext()}>
              {t('common.next')}
            </Button>
          )}
          {activeStep === 2 && (
            <Button
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
            >
              {t('csvImport.import')}
            </Button>
          )}
          {activeStep === 3 && (
            <Button variant="contained" onClick={handleClose}>
              {t('common.done')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
